import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  User, Mail, Shield, Zap, Clock, Calendar,
  Monitor, Smartphone, Laptop, Trash2, LogOut,
  Globe, AlertTriangle, Check, Loader2, RefreshCw, MapPin, Save,
  Building2, ArrowLeftRight, X
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function DeviceIcon({ os }) {
  if (!os) return <Monitor size={18} />;
  const lower = os.toLowerCase();
  if (lower.includes("android") || lower.includes("ios")) return <Smartphone size={18} />;
  if (lower.includes("mac") || lower.includes("windows") || lower.includes("linux")) return <Laptop size={18} />;
  return <Monitor size={18} />;
}

function timeAgo(dateStr) {
  if (!dateStr) return "Unknown";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function SettingsPage() {
  const { user, logoutAllDevices, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [maxDevices, setMaxDevices] = useState(3);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [removingDevice, setRemovingDevice] = useState(null);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [currentLocation, setCurrentLocation] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);

  const [ownedAgencies, setOwnedAgencies] = useState([]);
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [agencyForm, setAgencyForm] = useState({ name: "", handle: "", description: "" });
  const [handleStatus, setHandleStatus] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const mode = user?.workspace_mode || "candidate";

  const PREFS_KEY = "resumeai_defaults";

  const plan = user?.subscription?.plan || "trial";
  const isUnlimited = plan === "unlimited";
  const planLabel = plan === "trial" ? "Free Trial" : plan === "base" ? "Base Plan" : plan === "unlimited" ? "Unlimited" : plan;
  const planStatus = user?.subscription?.status || "active";
  const u = user?.usage || {};
  const optLimit = u.opt_limit ?? u.scans_limit ?? 0;
  const optUsed = u.opt_used ?? u.scans_used ?? 0;
  const optAddon = u.opt_addon ?? u.addon_scans ?? 0;
  const scanLimit = u.scan_limit ?? u.scans_limit ?? 0;
  const scanUsed = u.scan_used ?? 0;
  const scanAddon = u.scan_addon ?? 0;
  const trialExpires = user?.trial?.expires_at;
  const daysLeft = trialExpires
    ? Math.max(0, Math.ceil((new Date(trialExpires) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/devices`);
      setDevices(data.devices || []);
      setMaxDevices(data.max_devices || 3);
    } catch {
      /* silent */
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/sessions`);
      setSessions(data.sessions || []);
    } catch {
      /* silent */
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchSessions();
    axios.get(`${API}/api/agencies/mine`).then((r) => setOwnedAgencies(r.data.owned || [])).catch(() => {});
    // Fetch current location from profile
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/profile`);
        const loc = data.current_location || "";
        setCurrentLocation(loc);
        // Sync to localStorage
        try {
          const prefs = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
          prefs.candidateLocation = loc;
          localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
        } catch {}
      } catch {}
    })();
  }, [fetchDevices, fetchSessions]);

  async function handleSaveLocation() {
    setSavingLocation(true);
    try {
      await axios.put(`${API}/api/auth/profile`, { current_location: currentLocation });
      // Sync to localStorage so Optimize and Email pages pick it up
      try {
        const prefs = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
        prefs.candidateLocation = currentLocation;
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
      } catch {}
      toast.success("Location saved");
    } catch {
      toast.error("Failed to save location");
    } finally {
      setSavingLocation(false);
    }
  }

  async function handleRemoveDevice(deviceId) {
    setRemovingDevice(deviceId);
    try {
      await axios.delete(`${API}/api/auth/devices/${deviceId}`);
      toast.success("Device removed successfully");
      fetchDevices();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove device");
    } finally {
      setRemovingDevice(null);
    }
  }

  async function handleLogoutAllDevices() {
    setLoggingOutAll(true);
    try {
      await logoutAllDevices();
      toast.success("Logged out from all devices");
      navigate("/login");
    } catch {
      toast.error("Failed to logout from all devices");
      setLoggingOutAll(false);
    }
  }

  const checkHandle = useCallback(async (h) => {
    const handle = (h || "").trim().toLowerCase();
    if (handle.length < 3) { setHandleStatus(null); return; }
    try {
      const { data } = await axios.get(`${API}/api/agencies/handle-available?h=${encodeURIComponent(handle)}`);
      setHandleStatus(data.available ? "ok" : "bad");
    } catch { setHandleStatus(null); }
  }, []);

  async function handleRegisterAgency() {
    if (agencyForm.name.trim().length < 2) { toast.error("Agency name too short"); return; }
    if (handleStatus !== "ok") { toast.error("Pick an available @handle"); return; }
    setRegistering(true);
    try {
      const { data } = await axios.post(`${API}/api/agencies/register`, agencyForm);
      toast.success("Agency registered — switching to agency view");
      setShowAgencyModal(false);
      await refreshUser();
      navigate(`/app/agencies/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to register"); }
    finally { setRegistering(false); }
  }

  async function switchMode(target) {
    setSwitchingMode(true);
    try {
      await axios.post(`${API}/api/agencies/workspace-mode`, { mode: target });
      await refreshUser();
      toast.success(target === "agency" ? "Switched to agency view" : "Switched to candidate view");
      navigate("/app");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSwitchingMode(false); }
  }

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="settings-header">
        <h1>Account Settings</h1>
        <p>Manage your account, devices, and security.</p>
      </div>

      {/* Profile Section */}
      <div className="settings-section" data-testid="settings-profile">
        <h2><User size={18} /> Profile</h2>
        <div className="settings-grid">
          <div className="settings-field">
            <label>Full Name</label>
            <div className="settings-value" data-testid="settings-name">{user?.name || "N/A"}</div>
          </div>
          <div className="settings-field">
            <label>Email</label>
            <div className="settings-value" data-testid="settings-email">
              <Mail size={14} /> {user?.email || "N/A"}
            </div>
          </div>
          <div className="settings-field">
            <label>Member Since</label>
            <div className="settings-value" data-testid="settings-member-since">
              <Calendar size={14} /> {memberSince || "N/A"}
            </div>
          </div>
          <div className="settings-field settings-field-full">
            <label><MapPin size={14} /> Current Location</label>
            <p className="settings-field-hint">Used in job application emails and cover letters. Synced across all pages.</p>
            <div className="settings-location-row">
              <input
                type="text"
                className="settings-location-input"
                placeholder="e.g. Austin, TX"
                value={currentLocation}
                onChange={e => setCurrentLocation(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSaveLocation()}
                data-testid="settings-current-location"
              />
              <button
                className="settings-save-location-btn"
                onClick={handleSaveLocation}
                disabled={savingLocation}
                data-testid="settings-save-location-btn"
              >
                {savingLocation ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                {savingLocation ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Section */}
      <div className="settings-section" data-testid="settings-subscription">
        <h2><Zap size={18} /> Subscription</h2>
        <div className="settings-grid">
          <div className="settings-field">
            <label>Current Plan</label>
            <div className="settings-value">
              <span className="settings-plan-badge" data-testid="settings-plan">{planLabel}</span>
              <span className={`settings-status-badge ${planStatus}`} data-testid="settings-status">{planStatus}</span>
            </div>
          </div>
          <div className="settings-field">
            <label>Optimizations Used</label>
            <div className="settings-value" data-testid="settings-optimizations">
              {isUnlimited ? "Unlimited" : `${optUsed} / ${optLimit}`}
              {!isUnlimited && optAddon > 0 && <span className="settings-addon"> (+{optAddon} add-on)</span>}
            </div>
          </div>
          <div className="settings-field">
            <label>Scans Used</label>
            <div className="settings-value" data-testid="settings-scans">
              {isUnlimited ? "Unlimited" : `${scanUsed} / ${scanLimit}`}
              {!isUnlimited && scanAddon > 0 && <span className="settings-addon"> (+{scanAddon} add-on)</span>}
            </div>
          </div>
          {plan === "trial" && (
            <div className="settings-field">
              <label>Trial Expires</label>
              <div className="settings-value" data-testid="settings-trial-expires">
                <Clock size={14} /> {daysLeft} days remaining
              </div>
            </div>
          )}
        </div>
        {plan === "trial" && (
          <div className="settings-upgrade-banner" data-testid="settings-upgrade-banner">
            <div>
              <strong>Upgrade your plan</strong>
              <p>Get more scans and unlock premium features.</p>
            </div>
            <button className="settings-upgrade-btn" onClick={() => navigate("/app/pricing")} data-testid="settings-upgrade-btn">
              View Plans
            </button>
          </div>
        )}
      </div>

      {/* Agency Section */}
      <div className="settings-section" data-testid="settings-agency">
        <h2><Building2 size={18} /> Agency</h2>
        {ownedAgencies.length === 0 ? (
          <>
            <p className="settings-section-desc">
              Run a placement agency or recruit candidates? Register as an agency to get an organisation
              dashboard, teams, candidate analytics and join requests. Your job-hunt data stays safe and you can switch back anytime.
            </p>
            <button className="settings-upgrade-btn" data-testid="register-agency-btn" onClick={() => { setShowAgencyModal(true); setAgencyForm({ name: "", handle: "", description: "" }); setHandleStatus(null); }}>
              <Building2 size={15} /> Register as an Agency
            </button>
          </>
        ) : (
          <>
            <div className="settings-grid">
              <div className="settings-field">
                <label>Your agency</label>
                <div className="settings-value" data-testid="settings-agency-name">{ownedAgencies[0].name} <span style={{ color: "var(--text-muted)" }}>@{ownedAgencies[0].handle}</span></div>
              </div>
              <div className="settings-field">
                <label>Current view</label>
                <div className="settings-value">
                  <span className="settings-plan-badge" data-testid="settings-workspace-mode">{mode === "agency" ? "Agency view" : "Candidate view"}</span>
                </div>
              </div>
            </div>
            <div className="settings-upgrade-banner" data-testid="settings-mode-switch">
              <div>
                <strong>{mode === "agency" ? "You're in agency view" : "You're in candidate view"}</strong>
                <p>{mode === "agency" ? "Job-hunt features are hidden. Switch back to use your candidate dashboard." : "Switch to agency view to focus on your organisation dashboard."}</p>
              </div>
              {mode === "agency" ? (
                <button className="settings-upgrade-btn" data-testid="switch-to-candidate-btn" onClick={() => switchMode("candidate")} disabled={switchingMode}>
                  {switchingMode ? <Loader2 size={14} className="spin" /> : <ArrowLeftRight size={14} />} Switch to candidate view
                </button>
              ) : (
                <button className="settings-upgrade-btn" data-testid="switch-to-agency-btn" onClick={() => switchMode("agency")} disabled={switchingMode}>
                  {switchingMode ? <Loader2 size={14} className="spin" /> : <ArrowLeftRight size={14} />} Switch to agency view
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Devices Section */}
      <div className="settings-section" data-testid="settings-devices-section">
        <div className="settings-section-header">
          <h2><Monitor size={18} /> Registered Devices</h2>
          <span className="settings-device-count" data-testid="settings-device-count">
            {devices.length} / {maxDevices}
          </span>
        </div>
        <p className="settings-section-desc">
          Your account can be registered on up to {maxDevices} devices. Only one session can be active at a time.
        </p>

        {loadingDevices ? (
          <div className="settings-loading"><Loader2 size={20} className="spin" /> Loading devices...</div>
        ) : devices.length === 0 ? (
          <div className="settings-empty">No devices registered yet.</div>
        ) : (
          <div className="settings-device-list" data-testid="settings-device-list">
            {devices.map((d) => (
              <div key={d.device_id} className={`settings-device-card ${d.is_current ? "current" : ""}`} data-testid={`device-${d.device_id}`}>
                <div className="settings-device-icon">
                  <DeviceIcon os={d.os} />
                </div>
                <div className="settings-device-info">
                  <div className="settings-device-name">
                    {d.name || "Unknown device"}
                    {d.is_current && <span className="settings-current-badge" data-testid="current-device-badge"><Check size={10} /> This device</span>}
                  </div>
                  <div className="settings-device-meta">
                    <span><Globe size={12} /> {d.ip_masked}</span>
                    <span><Clock size={12} /> Last active: {timeAgo(d.last_used)}</span>
                  </div>
                </div>
                <div className="settings-device-actions">
                  {!d.is_current && (
                    <button
                      className="settings-device-remove-btn"
                      onClick={() => handleRemoveDevice(d.device_id)}
                      disabled={removingDevice === d.device_id}
                      data-testid={`remove-device-${d.device_id}`}
                      title="Remove this device"
                    >
                      {removingDevice === d.device_id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sessions Section */}
      <div className="settings-section" data-testid="settings-sessions-section">
        <div className="settings-section-header">
          <h2><Shield size={18} /> Recent Sessions</h2>
          <button className="settings-refresh-btn" onClick={() => { setLoadingSessions(true); fetchSessions(); }} data-testid="refresh-sessions-btn">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loadingSessions ? (
          <div className="settings-loading"><Loader2 size={20} className="spin" /> Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div className="settings-empty">No session history.</div>
        ) : (
          <div className="settings-session-list" data-testid="settings-session-list">
            {sessions.slice(0, 10).map((s, i) => (
              <div key={s.session_id || i} className={`settings-session-row ${s.is_active ? "active" : ""}`}>
                <div className="settings-session-status">
                  <span className={`settings-session-dot ${s.is_active ? "active" : "inactive"}`} />
                </div>
                <div className="settings-session-info">
                  <span className="settings-session-device">{s.browser || "Unknown"} on {s.os || "Unknown"}</span>
                  <span className="settings-session-meta">
                    <Globe size={11} /> {s.ip_masked} &middot; {new Date(s.login_time).toLocaleString()}
                  </span>
                </div>
                <div className="settings-session-badge">
                  {s.is_current ? (
                    <span className="settings-current-badge"><Check size={10} /> Current</span>
                  ) : s.is_active ? (
                    <span className="settings-active-badge">Active</span>
                  ) : (
                    <span className="settings-inactive-badge">Ended</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="settings-section settings-danger-zone" data-testid="settings-danger-zone">
        <h2><AlertTriangle size={18} /> Security Actions</h2>
        <div className="settings-danger-actions">
          <div className="settings-danger-item">
            <div>
              <strong>Logout from all devices</strong>
              <p>This will end all sessions and remove all registered devices. You will need to log in again.</p>
            </div>
            <button
              className="settings-danger-btn"
              onClick={handleLogoutAllDevices}
              disabled={loggingOutAll}
              data-testid="logout-all-devices-btn"
            >
              {loggingOutAll ? <Loader2 size={14} className="spin" /> : <LogOut size={14} />}
              {loggingOutAll ? "Logging out..." : "Logout All Devices"}
            </button>
          </div>
        </div>
      </div>

      {/* Register agency modal */}
      {showAgencyModal && (
        <div className="modal-overlay" data-testid="register-agency-modal" onClick={() => setShowAgencyModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(460px, 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Building2 size={20} /> Register as an Agency</h2>
              <button onClick={() => setShowAgencyModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>This switches your account to agency view. Your candidate data is preserved and you can switch back from Settings.</p>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Agency name</label>
            <input className="modal-input" data-testid="agency-reg-name" value={agencyForm.name} maxLength={80}
              onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })} placeholder="CareerLaunch Agency" style={{ width: "100%", marginBottom: 12 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Handle (@)</label>
            <input className="modal-input" data-testid="agency-reg-handle" value={agencyForm.handle}
              onChange={(e) => { const v = e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ""); setAgencyForm({ ...agencyForm, handle: v }); checkHandle(v); }}
              placeholder="careerlaunch" style={{ width: "100%", marginBottom: 4 }} />
            {handleStatus === "ok" && <div data-testid="agency-handle-ok" style={{ fontSize: "0.76rem", color: "#10b981", marginBottom: 10 }}><Check size={12} /> @{agencyForm.handle} is available</div>}
            {handleStatus === "bad" && <div data-testid="agency-handle-bad" style={{ fontSize: "0.76rem", color: "#ef4444", marginBottom: 10 }}>That handle is taken or invalid</div>}
            {!handleStatus && <div style={{ marginBottom: 10 }} />}
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Description (optional)</label>
            <input className="modal-input" data-testid="agency-reg-desc" value={agencyForm.description} maxLength={300}
              onChange={(e) => setAgencyForm({ ...agencyForm, description: e.target.value })} placeholder="What does your agency do?" style={{ width: "100%", marginBottom: 16 }} />
            <button className="btn-primary" data-testid="agency-reg-submit" onClick={handleRegisterAgency} disabled={registering} style={{ width: "100%" }}>
              {registering ? <Loader2 size={16} className="spin" /> : <Building2 size={16} />} Register & switch to agency view
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
